import { useNavigate, useSearchParams } from "@solidjs/router"
import { createMemo, createSignal, Show } from "solid-js"
import { useAppContext } from "@/context/app"
import { Box } from "@/designSystem/components/box"
import { Button } from "@/designSystem/components/button"
import { Well } from "@/designSystem/components/well"
import { MainLayout } from "@/designSystem/layouts/mainLayout"
import { Section } from "@/designSystem/patterns/section"
import { StoryComponent } from "@/designSystem/patterns/story"
import { createStoriesRouter } from "@/routes/stories-router"
import { AddStoryForm } from "./add-story-form"

function extractFirstUrl(text: string | undefined) {
  if (!text) return null

  const urlRegex =
    /(\bhttps?:\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gim
  const match = text.match(urlRegex)
  return match ? match[0] : null // Return the first URL found or null if no URLs are found
}

export function Share() {
  const { db, replicache } = useAppContext()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [deleting, setDeleting] = createSignal<boolean>(false)
  const { paths } = createStoriesRouter()

  const getUrl = createMemo(
    () =>
      (searchParams.url as string) ||
      extractFirstUrl(searchParams.text as string) ||
      extractFirstUrl(searchParams.title as string),
  )

  const getStory = createMemo(() => {
    const url = getUrl()
    if (!url) return null

    return db.story.findBy({ url: url })
  })

  const getBookmark = createMemo(() => {
    const story = getStory()
    if (!story) return null

    return db.bookmark.findBy({ story_id: story.id })
  })

  async function onDelete() {
    const bookmark = getBookmark()
    if (!bookmark) return null

    setDeleting(true)
    await replicache.mutate.deleteBookmark(bookmark.id)
    try {
      browser.runtime.sendMessage(import.meta.env.VITE_EXTENSION_ID, {
        deletedBookmark: getUrl(),
      })
    } catch (_e) {
      navigate(paths().url)
    }
  }

  return (
    <MainLayout>
      <Show when={getBookmark()} fallback={<AddStoryForm url={getUrl()} />}>
        <Section
          title="this story is already bookmarked"
          hue="emerald"
          iconName="book-marked"
          description="it looks like you've already saved this story in the past."
        >
          <Well hue="sand">
            <StoryComponent story={getStory()!} />
            <Box padding={{ inline: "16" }} alignSelf="flex-end">
              <Button
                onClick={onDelete}
                hue="rose"
                iconName="trash-2"
                disabled={deleting()}
              >
                {deleting() ? "deleting..." : "delete story"}
              </Button>
            </Box>
          </Well>
        </Section>
      </Show>
    </MainLayout>
  )
}
